/**
 * Draws the right-click menu of `state/menu.ts`: kept inside the window, with sub-menus
 * (hover, or the right arrow), and usable from the keyboard (arrows, Enter, Escape).
 *
 * It closes after a choice, on Escape, on a click elsewhere, or when the window loses focus
 * or changes size.
 */
import { For, Show, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { useApp } from "./context";
import { isItem, type MenuEntry, type MenuItem } from "../state/menu";

/** Gap kept between a menu and the window's edges. */
const EDGE = 4;

/** The menu, when one is open. */
export function ContextMenuView(): JSX.Element {
  const { menu } = useApp();
  onMount(() => {
    /** A press outside every menu panel closes the menu. */
    const outside = (e: PointerEvent) => {
      // The menu bar's titles open and close their menus themselves.
      if (!(e.target instanceof Element && e.target.closest(".ctx-menu, .mb-title"))) menu.close();
    };
    const close = () => menu.close();
    /** Escape closes the menu wherever the focus is, before any shortcut sees it. */
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !menu.state()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      menu.close();
    };
    addEventListener("keydown", escape, true);
    addEventListener("pointerdown", outside, true);
    addEventListener("blur", close);
    addEventListener("resize", close);
    onCleanup(() => {
      removeEventListener("keydown", escape, true);
      removeEventListener("pointerdown", outside, true);
      removeEventListener("blur", close);
      removeEventListener("resize", close);
    });
  });
  return (
    <Show when={menu.state()} keyed>
      {(state) => <Panel entries={state.entries} x={state.x} y={state.y} owner={state.owner} focusFirst />}
    </Show>
  );
}

/** One panel of the menu (the main one or a sub-menu) whose top-left corner is near (x, y). */
function Panel(props: { entries: MenuEntry[]; x: number; y: number; owner?: string | null; focusFirst?: boolean; leftOf?: number; onBack?: () => void }): JSX.Element {
  const { menu, commands, keys } = useApp();
  let panel!: HTMLDivElement;
  const [place, setPlace] = createSignal({ left: props.x, top: props.y, ready: false });
  /** The item whose sub-menu is open, and where. */
  const [sub, setSub] = createSignal<{ item: MenuItem; x: number; y: number; left: number; focus: boolean } | null>(null);
  /** The item clicked once that asks to be clicked again (`confirm`). */
  const [armed, setArmed] = createSignal<MenuItem | null>(null);

  onMount(() => {
    // Measured once drawn, then moved inside the window (a sub-menu flips to the left).
    const r = panel.getBoundingClientRect();
    let left = props.x;
    if (left + r.width > innerWidth - EDGE) left = props.leftOf !== undefined ? props.leftOf - r.width : innerWidth - EDGE - r.width;
    const top = Math.min(props.y, innerHeight - EDGE - r.height);
    setPlace({ left: Math.max(EDGE, left), top: Math.max(EDGE, top), ready: true });
    // Focused once visible (a hidden element cannot take the focus).
    if (props.focusFirst) requestAnimationFrame(() => buttons()[0]?.focus());
  });

  /** The enabled buttons of this panel, in order. */
  const buttons = () => [...panel.querySelectorAll<HTMLButtonElement>(":scope > button:not(:disabled)")];

  /** The shortcut of a command, as written in the config (`Ctrl+Z`). */
  const shortcut = (item: MenuItem) => keys.shortcut(item.command, item.argument ?? null);

  /** Runs an item and closes the whole menu. */
  const run = (item: MenuItem) => {
    if (item.disabled || item.items) return;
    if (item.confirm && armed() !== item) {
      setArmed(item);
      return;
    }
    menu.close();
    if (item.action) item.action();
    else if (item.command) commands.run(item.command, item.argument ?? null);
  };

  /** Opens an item's sub-menu beside its line. */
  const openSub = (item: MenuItem, button: HTMLButtonElement, focus: boolean) => {
    const r = button.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    setSub({ item, x: p.right - 2, y: r.top - 5, left: p.left + 2, focus });
  };

  /** Arrow keys move between lines and in and out of sub-menus. */
  const onKey = (e: KeyboardEvent) => {
    const list = buttons();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      list[(at + step + list.length) % list.length]?.focus();
    } else if (e.key === "ArrowRight") {
      const item = list[at] && itemOf(list[at]);
      if (item?.items) {
        e.preventDefault();
        openSub(item, list[at]!, true);
      } else if (props.owner) {
        e.preventDefault();
        menu.step(1);
      }
    } else if (e.key === "ArrowLeft" && props.onBack) {
      e.preventDefault();
      props.onBack();
    } else if (e.key === "ArrowLeft" && props.owner) {
      e.preventDefault();
      menu.step(-1);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      menu.close();
    } else return;
    e.stopPropagation();
  };

  const items = new WeakMap<HTMLButtonElement, MenuItem>();
  /** The item a button stands for. */
  const itemOf = (button: HTMLButtonElement) => items.get(button);

  return (
    <div
      ref={panel}
      class="ctx-menu"
      id={props.onBack ? undefined : props.owner ? `menu-${props.owner}` : "context-menu"}
      data-owner={props.owner ?? undefined}
      role="menu"
      style={{ left: `${place().left}px`, top: `${place().top}px`, visibility: place().ready ? "visible" : "hidden" }}
      onKeyDown={onKey}
      onContextMenu={(e) => e.preventDefault()}
    >
      <For each={props.entries}>
        {(entry) => {
          if ("separator" in entry) return <hr />;
          if ("heading" in entry) return <div class="ctx-h">{entry.heading}</div>;
          if (!isItem(entry)) return null;
          return (
            <button
              type="button"
              role={entry.checked === undefined ? "menuitem" : "menuitemcheckbox"}
              aria-checked={entry.checked}
              aria-haspopup={entry.items ? "menu" : undefined}
              classList={{ danger: !!entry.danger || armed() === entry, open: sub()?.item === entry }}
              disabled={entry.disabled}
              data-menu-item={entry.label}
              ref={(el) => items.set(el, entry)}
              onPointerEnter={(e) => (entry.items ? openSub(entry, e.currentTarget, false) : setSub(null))}
              onPointerLeave={() => armed() === entry && setArmed(null)}
              onClick={(e) => (entry.items ? openSub(entry, e.currentTarget, true) : run(entry))}
            >
              <span class="ctx-check">{entry.checked === undefined ? "" : entry.checked ? "✓" : ""}</span>
              <span class="ctx-label">{armed() === entry ? entry.confirm : entry.label}</span>
              <span class="ctx-key">{entry.items ? "›" : shortcut(entry)}</span>
            </button>
          );
        }}
      </For>
      <Show when={sub()} keyed>
        {(s) => (
          <Panel
            entries={s.item.items ?? []}
            x={s.x}
            y={s.y}
            leftOf={s.left}
            focusFirst={s.focus}
            onBack={() => {
              setSub(null);
              buttons().find((b) => itemOf(b) === s.item)?.focus();
            }}
          />
        )}
      </Show>
    </div>
  );
}
